import { BrowserProtocol, createBasenameMiddleware, queryMiddleware } from 'farce';
import {
	type CreateRenderArgs,
	createFarceRouter,
	HttpError,
	makeRouteConfig,
	Redirect,
	resolver,
} from 'found';
import React from 'react';

import AppPage from '../AppPage';
import { AI_TOOLS_TEMPLATE_SEGMENT } from '../components/AIAssistant/shared/templateSlugs';
import {
	CORRECTIONS_EVALUATION_SEGMENT,
	CORRECTIONS_STUDENT_WORK_SEGMENT,
	type RouteDescriptor,
	routes,
} from '../constants';
import Error404 from '../routes/Error404';
import { notifyBugsnagHandledError } from '../utils/bugsnagClient';
import AuthenticatedRoute from './AuthenticatedRoute';
import Route from './Route';
import WalkInRestrictedRoute from './WalkInRestrictedRoute';

const getRouteConfig = (config: RouteDescriptor) => {
	let path = config.root;
	if (config.param) {
		path = `${path}/:${config.param}`;
	} else if (config.optionalParam) {
		path = `${path}/:${config.optionalParam}?`;
	}
	return {
		title: config.documentTitle,
		path,
	};
};

const getBackwardCompatibilityRedirects = () => (
	<>
		<Redirect
			from="/angebote/12fuer10"
			to={
				'/zahlungsdaten?data=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJsaWNlbmNlSWQiOjEzMn0' +
				'.IUKeDfyhOud0vwgDmr5sZGTSiNjH7IN6XeuCSxyG5ys&promoCode=schulstart22'
			}
		/>
		<Redirect
			from="/angebote/12fuer10-Ref"
			to={
				'/zahlungsdaten?data=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJsaWNlbmNlSWQiOjQxfQ' +
				'.VIzjPXGWBeOAvfKTVUNbOb0apCRon9uV7d4YxTeSMLE&promoCode=schulstart22-ref'
			}
		/>
	</>
);

// Returns the component synchronously once its chunk has loaded: found's
// resolver only enters the pending state (which suspends rendering) when
// getComponent returns a promise. Without the memo, every navigation —
// including a param-only change like a /suche tab switch — re-awaits the
// import() and flashes the pending state, unmounting the whole page tree.
export const lazyComponent = <P,>(load: () => Promise<{ default: React.ComponentType<P> }>) => {
	let loaded: React.ComponentType<P> | undefined;
	let pending: Promise<React.ComponentType<P>> | undefined;
	return () => {
		if (loaded) {
			return loaded;
		}
		if (!pending) {
			pending = load().then(
				(m) => {
					loaded = m.default;
					return loaded;
				},
				(error) => {
					// Cleared so a later navigation retries after a failed chunk load
					// instead of being stuck on the rejected promise forever.
					pending = undefined;
					notifyBugsnagHandledError(error instanceof Error ? error : new Error(String(error)));
					// found only routes HttpError rejections into renderError — a raw
					// import() failure escapes resolveMatch as an unhandled rejection,
					// leaving no error page (blank page on a full load).
					const httpError = new HttpError(500);
					httpError.data = error;
					throw httpError;
				}
			);
		}
		return pending;
	};
};

// Hoisted for the routes that share one component, so they also share one
// memo cache — separate resolvers would re-enter the pending state when
// navigating between them (e.g. /school/dashboard → /school/admins).
const getSchoolAdmin = lazyComponent(() => import('../components/SchoolAdmin/SchoolAdmin'));
const getDeprecatedRegisterPremiumPage = lazyComponent(
	() => import('../routes/register/DeprecatedRegisterPremiumPage')
);

// Page routes use getComponent dynamic imports so each page is its own lazy
// chunk and the initial module graph stays shell-only (PLA-85). New page
// routes must follow suit — router.test.tsx fails on static Component pages.
const RouteContainer = (
	<Route
		path="/"
		Component={AppPage}
		render={({ Component, props }) => {
			// This 'render' property is being used to stop Found-router
			// from continuously un-mounting all of the apps components
			// on each route change (while navigation).
			// Hence, it shouldn't be removed.
			if (Component) return <Component {...props} />;
			return null;
		}}
	>
		<Redirect from="/" to={`/${routes.dashboard.root}`} />
		{getBackwardCompatibilityRedirects()}

		<AuthenticatedRoute>
			<WalkInRestrictedRoute>
				<Route
					path={`${routes.quickDownload.root}/:mindItemId`}
					title={routes.quickDownload.documentTitle}
					getComponent={lazyComponent(() => import('../routes/QuickDownload'))}
				/>
				<Route path={routes.collections.root}>
					<Route
						getComponent={lazyComponent(() => import('../routes/OwnCollectionListPage'))}
						title={routes.collections.documentTitle}
					/>
					<Route
						getComponent={lazyComponent(() => import('../routes/OwnCollectionItemListPage'))}
						path=":collectionId"
						title={routes.collections.documentTitle}
					/>
				</Route>
				<Route
					{...getRouteConfig(routes.pinned)}
					getComponent={lazyComponent(() => import('../routes/PinnedItemListPage'))}
				/>
				<Route
					{...getRouteConfig(routes.downloads)}
					getComponent={lazyComponent(() => import('../routes/DownloadListPage'))}
				/>
				<Route path={routes.curatedCollections.root}>
					<Route
						getComponent={lazyComponent(() => import('../routes/CuratedCollectionListPage'))}
						title={routes.curatedCollections.documentTitle}
					/>
					<Route
						getComponent={lazyComponent(() => import('../routes/CuratedCollectionItemListPage'))}
						path=":collectionId"
						title={routes.curatedCollections.documentTitle}
					/>
				</Route>
				<Route path={routes.followed.root}>
					<Route
						getComponent={lazyComponent(() => import('../routes/FollowedCollectionListPage'))}
						title={routes.followed.documentTitle}
					/>
					<Route
						getComponent={lazyComponent(() => import('../routes/FollowedCollectionItemListPage'))}
						path=":collectionId"
						title={routes.followed.documentTitle}
					/>
				</Route>
				<Route
					{...getRouteConfig(routes.dashboard)}
					getComponent={lazyComponent(() => import('../routes/DashboardPage'))}
				/>
				<Route
					path={routes.aiTools.root}
					title={routes.aiTools.documentTitle}
					getComponent={lazyComponent(() => import('../routes/AIToolsPage'))}
				>
					<Route path={`${AI_TOOLS_TEMPLATE_SEGMENT}/:templateSlug`} />
					<Route path={`:${routes.aiTools.optionalParam}?`} />
				</Route>
				<Route
					{...getRouteConfig(routes.aiAssistantHistory)}
					getComponent={lazyComponent(() => import('../routes/AIAssistantHistoryPage/HistoryPage'))}
				/>
				<Route path={routes.corrections.root}>
					<Route
						getComponent={lazyComponent(() => import('../routes/CorrectionsPage'))}
						title={routes.corrections.documentTitle}
					/>
					<Route
						path={CORRECTIONS_EVALUATION_SEGMENT}
						getComponent={lazyComponent(() => import('../routes/CorrectionsEvaluationPage'))}
						title={routes.corrections.documentTitle}
					/>
					<Route
						path={CORRECTIONS_STUDENT_WORK_SEGMENT}
						getComponent={lazyComponent(() => import('../routes/CorrectionsStudentWorkPage'))}
						title={routes.corrections.documentTitle}
					/>
				</Route>
				<Route
					{...getRouteConfig(routes.personalDetails)}
					getComponent={lazyComponent(() => import('../routes/PersonalDetailsPage'))}
				/>
				<Route
					{...getRouteConfig(routes.courseCatalog)}
					getComponent={lazyComponent(
						() => import('../routes/courses/CourseSearchPage/CourseSearchPage')
					)}
				/>
				<Route
					{...getRouteConfig(routes.courseCover)}
					getComponent={lazyComponent(() => import('../routes/courses/CourseCoverPage/CourseCoverPage'))}
				/>
				<Route
					{...getRouteConfig(routes.coursePlayer)}
					getComponent={lazyComponent(
						() => import('../routes/courses/CoursePlayerPage/CoursePlayerPage')
					)}
				/>
				<Route
					{...getRouteConfig(routes.myCertificates)}
					getComponent={lazyComponent(
						() => import('../routes/courses/MyCertificatesPage/MyCertificatesPage')
					)}
				/>
				<Route
					{...getRouteConfig(routes.courseFinishPage)}
					getComponent={lazyComponent(
						() => import('../routes/courses/CourseFinishPage/CourseFinishPage')
					)}
				/>
				<Route
					{...getRouteConfig(routes.courseAttachments)}
					getComponent={lazyComponent(
						() => import('../routes/courses/CourseAttachmentsPage/CourseAttachmentsPage')
					)}
				/>
				<Route
					{...getRouteConfig(routes.myCourses)}
					getComponent={lazyComponent(() => import('../routes/courses/MyCoursesPages/MyCoursesPage'))}
				/>
				<Route
					{...getRouteConfig(routes.myContents)}
					getComponent={lazyComponent(() => import('../routes/MyContentsPage/MyContentsPage'))}
				/>
			</WalkInRestrictedRoute>
			<Route
				{...getRouteConfig(routes.search)}
				getComponent={lazyComponent(() => import('../routes/SearchPage'))}
			/>
			<Route
				{...getRouteConfig(routes.productPage)}
				getComponent={lazyComponent(() => import('../routes/ProductPage'))}
			/>
			<Route
				title={routes.productAttachmentsPage.documentTitle}
				path={`${routes.productAttachmentsPage.root}/:productId/:attachmentsPath*`}
				getComponent={lazyComponent(() => import('../routes/ProductAttachmentsPage'))}
			/>
			<Route
				path={`${routes.reader.root}/:documentId`}
				getComponent={lazyComponent(() => import('../routes/ReaderPage'))}
			/>
		</AuthenticatedRoute>
		<Route
			{...getRouteConfig(routes.login)}
			getComponent={lazyComponent(() => import('../routes/login/LoginPage'))}
		/>
		<Route
			{...getRouteConfig(routes.resetPassword)}
			getComponent={lazyComponent(() => import('../routes/login/NewResetPasswordPage'))}
		/>
		<Route
			{...getRouteConfig(routes.setPassword)}
			getComponent={lazyComponent(() => import('../routes/login/NewSetPasswordPage'))}
			defer={true}
		/>
		<Route
			{...getRouteConfig(routes.resetPasswordConfirmed)}
			getComponent={lazyComponent(() => import('../routes/login/NewResetPasswordConfirmedPage'))}
		/>
		<Route
			{...getRouteConfig(routes.checkoutSuccess)}
			getComponent={lazyComponent(() => import('../routes/checkout/CheckoutSuccessPage'))}
		/>
		<Route
			{...getRouteConfig(routes.payment)}
			getComponent={lazyComponent(
				() => import('../routes/checkout/StripeCheckoutPage/GuardedStripeCheckoutPage')
			)}
		/>
		<Route
			{...getRouteConfig(routes.setNewEmail)}
			getComponent={lazyComponent(() => import('../routes/ChangeEmailPage'))}
			defer={true}
		/>
		<Route
			{...getRouteConfig(routes.changeEmailConfirm)}
			getComponent={lazyComponent(() => import('../routes/ChangeEmailConfirm'))}
			defer={true}
		/>
		<Route
			{...getRouteConfig(routes.ipRangeError)}
			getComponent={lazyComponent(() => import('../routes/login/IpRangeErrorPage'))}
		/>
		<Route
			{...getRouteConfig(routes.keycloakRedirect)}
			getComponent={lazyComponent(() => import('../routes/KeycloakSSORedirect/KeycloakSSORedirect'))}
		/>
		<Route
			{...getRouteConfig(routes.onboardingPage)}
			getComponent={lazyComponent(() => import('../routes/onboarding/OnboardingPage'))}
		/>
		<AuthenticatedRoute>
			<Route {...getRouteConfig(routes.upgradePackageSelection)}>
				<Route
					title={routes.upgradePackageSelection.documentTitle}
					getComponent={lazyComponent(() => import('../routes/upgrade/PricingPage'))}
				/>
				<Redirect
					from={routes.deprecatedUpgradeRef.root}
					to={`/${routes.upgradePackageSelection.root}`}
				/>
			</Route>
			<Route {...getRouteConfig(routes.stripeUpgrade)}>
				<Route
					title={routes.stripeUpgrade.documentTitle}
					getComponent={lazyComponent(() => import('../routes/upgrade/StripeUpgradePage'))}
				/>
				<Route {...getRouteConfig(routes.stripeUpgradeConfirm)}>
					<Route
						title={routes.stripeUpgradeConfirm.documentTitle}
						getComponent={lazyComponent(() => import('../routes/upgrade/StripeUpgradeConfirmPage'))}
					/>
					<Route
						{...getRouteConfig(routes.stripeUpgradeCheckout)}
						getComponent={lazyComponent(() => import('../routes/upgrade/StripeResubscribeCheckoutPage'))}
					/>
				</Route>
			</Route>
			<Redirect
				from={`${routes.deprecatedUpgradeOfferCode.root}*`}
				to={`/${routes.upgradePackageSelection.root}`}
			/>
			<Route
				{...getRouteConfig(routes.upgradePayment)}
				getComponent={lazyComponent(
					() => import('../routes/SinglePagePayment/PaymentPage/UpgradePaymentPage')
				)}
			/>
			<Redirect
				from={routes.deprecatedUpgradeConfirm.root}
				to={`/${routes.upgradePackageSelection.root}`}
			/>
		</AuthenticatedRoute>
		<Redirect
			from={routes.deprecatedPremiumTrialRegistration.root}
			to={(url) => {
				const {
					location: { query },
				} = url;
				return {
					pathname: `/${routes.register.root}/${routes.registerPremiumCreateAccount.root}`,
					query,
				};
			}}
		/>
		<Route {...getRouteConfig(routes.register)}>
			<Route
				{...getRouteConfig(routes.registerPremiumCreateAccount)}
				getComponent={lazyComponent(
					() => import('../routes/SinglePagePayment/PaymentPage/RegisterPremiumPaymentPage')
				)}
				defer={true}
			/>
			<Route
				{...getRouteConfig(routes.deprecatedRegisterPremiumPackageSelection)}
				getComponent={getDeprecatedRegisterPremiumPage}
			/>
			<Route
				{...getRouteConfig(routes.deprecatedRegisterPremiumPayment)}
				getComponent={getDeprecatedRegisterPremiumPage}
			/>
			<Route
				{...getRouteConfig(routes.deprecatedRegisterPremiumConfirm)}
				getComponent={getDeprecatedRegisterPremiumPage}
			/>
		</Route>
		<Route
			path={`${routes.publicReader.root}/:accessToken`}
			getComponent={lazyComponent(() => import('../routes/PublicVideoPlayerPage'))}
		/>
		<Route
			{...getRouteConfig(routes.generatedDocumentPublicView)}
			getComponent={lazyComponent(() => import('../routes/PublicGeneratedDocumentPage'))}
		/>
		<Route
			{...getRouteConfig(routes.podcastTranscriptPublicView)}
			getComponent={lazyComponent(() => import('../routes/PublicPodcastTranscriptPage'))}
		/>
		<AuthenticatedRoute>
			<Route path="/school/dashboard" getComponent={getSchoolAdmin}>
				<Route
					getComponent={lazyComponent(() => import('../components/SchoolAdmin/SchoolDashboard'))}
				/>
			</Route>
			<Route path="/school/admins" getComponent={getSchoolAdmin}>
				<Route
					getComponent={lazyComponent(
						() => import('../components/SchoolAdmin/SchoolAdmins/SchoolAdminsList/SchoolAdminsList')
					)}
				/>
			</Route>
			<Route path="/school/teachers" getComponent={getSchoolAdmin}>
				<Route
					getComponent={lazyComponent(
						() => import('../components/StripeSchoolAdmin/LegacyTeachersRoute/LegacyTeachersRoute')
					)}
				/>
			</Route>
			<Route path="/stripe-school/teachers" getComponent={getSchoolAdmin}>
				<Route
					getComponent={lazyComponent(
						() => import('../components/StripeSchoolAdmin/StripeTeachersRoute/StripeTeachersRoute')
					)}
				/>
			</Route>
			<Route path="/school/license" getComponent={getSchoolAdmin}>
				<Route
					getComponent={lazyComponent(() => import('../components/SchoolAdmin/SchoolLicenceBilling'))}
				/>
			</Route>
		</AuthenticatedRoute>
	</Route>
);

const routeConfig = makeRouteConfig(RouteContainer);

const renderError: CreateRenderArgs['renderError'] = ({ error }) => {
	return <AppPage>{error.status === 404 ? <Error404 /> : 'Got error, please refresh.'}</AppPage>;
};

const basenameMiddleware = createBasenameMiddleware({
	basename: process.env.BASENAME,
});

const FarceRouter = createFarceRouter({
	historyMiddlewares: [queryMiddleware, basenameMiddleware],
	historyProtocol: new BrowserProtocol(),
	renderError,
	routeConfig,
});

export { routeConfig };

export default () => <FarceRouter resolver={resolver} />;
